/**
 * The one place that talks to the server.
 *
 * Every call sends the session cookie and normalises failures into a thrown
 * ApiError, so pages can use try/catch rather than each inventing its own
 * shape for "did that work".
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * `body` is widened to unknown and JSON-encoded here, so callers pass objects
 * rather than pre-stringified text. RequestInit's own body type has to be
 * omitted first — intersecting the two would require a value that satisfies
 * both BodyInit and whatever was passed.
 */
async function request<T>(
  path: string,
  init: Omit<RequestInit, "body"> & { body?: unknown } = {},
): Promise<T> {
  const { body, ...rest } = init;

  const res = await fetch(path, {
    ...rest,
    credentials: "same-origin",
    headers: {
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      ...rest.headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // A non-JSON body from a proxy or a crash — the status still tells us enough.
  }

  if (!res.ok) {
    const message =
      (payload as { error?: string } | null)?.error ??
      `Something went wrong (${res.status}).`;
    throw new ApiError(message, res.status);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
